import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from './Navbar';
import { ClompApi } from '../api/ClompApi';
import { getVolume, isMuted, playGoldenCatch, playMugClick, setMuted, setVolume } from './clickerSounds';
import './ClompyClicker.css';

const api = new ClompApi();

// How often clicks are sent to the backend while the game is being played
const SYNC_MS = 2000;
// With no clicks to send, time played is still sent once this many seconds have built up
const IDLE_SYNC_SECONDS = 15;

// The golden diamond: how long after the game opens the first can appear, how long between later ones, and how long
// one stays before it gets away (all in seconds of the game being on screen; each wait is picked between two ends).
// The backend pays for one a minute at most, so the gaps stay above that.
const GOLDEN_FIRST = [45, 90];
const GOLDEN_GAP = [75, 150];
const GOLDEN_STAYS = 10;
// What one pays a guest, whose diamonds aren't saved; a logged-in player's is worked out by the backend
const GOLDEN_GUEST_REWARD = 25;

// Coming back to the tab after it was out of sight this long asks the backend what was earned meanwhile (the backend
// decides for itself whether the player was really away)
const AWAY_CHECK_MS = 60 * 1000;

// What a player has before buying anything, and all a guest ever has
const STARTING_GAME = {
    levels: { sips: 0, machine: 0, barista: 0, thermos: 0 },
    owned: ['mug_classic', 'bg_night', 'fx_plus'],
    equipped: { mug: 'mug_classic', background: 'bg_night', effect: 'fx_plus' },
    perClick: 1,
    perSecond: 0,
    awayPerHour: 0,
    awayMaxSeconds: 3600
};

// How each mug the shop sells is coloured. The To-Go Cup (mug_togo) has a drawing of its own: see ToGoCup.
const MUGS = {
    mug_classic: { body: '#b0e0e6', shade: '#8fcdd6', light: '#e6fbfd', stripe: '#ff7a1a', outline: '#16222e', face: '#16222e', cheeks: '#ff9d8a' },
    mug_alien: { body: '#62e06a', shade: '#43b84c', light: '#d0ffd4', stripe: '#2d7a35', outline: '#12301a', face: '#12301a', cheeks: '#b6ff6a' },
    mug_gold: { body: '#ffd56a', shade: '#e0a93a', light: '#fff4c8', stripe: '#b07e1a', outline: '#4a3208', face: '#4a3208', cheeks: '#ff9d6a' }
};

// The mark each click effect puts before the number that floats up from a click
const EFFECTS = {
    fx_plus: '',
    fx_stars: '★ ',
    fx_hearts: '♥ '
};

function between([low, high]) {
    return low + Math.floor(Math.random() * (high - low + 1));
}

// Seconds as a rough length of time, for the welcome back panel: 45 seconds, 12 minutes, 3 hours 20 minutes, 2 days 4 hours
function formatAway(seconds) {
    let some = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;
    let minutes = Math.floor(seconds / 60);
    let hours = Math.floor(minutes / 60);
    if (minutes < 1) {
        return some(seconds, 'second');
    }
    if (hours < 1) {
        return some(minutes, 'minute');
    }
    if (hours < 24) {
        return some(hours, 'hour') + (minutes % 60 ? ` ${some(minutes % 60, 'minute')}` : '');
    }
    return some(Math.floor(hours / 24), 'day') + (hours % 24 ? ` ${some(hours % 24, 'hour')}` : '');
}

// Seconds as a length of time: 42s, 5m 07s, 2h 05m 07s
function formatDuration(seconds) {
    let pad = (n) => (n < 10 ? '0' : '') + n;
    let s = seconds % 60;
    let m = Math.floor(seconds / 60) % 60;
    let h = Math.floor(seconds / 3600);
    return h > 0 ? `${h.toLocaleString()}h ${pad(m)}m ${pad(s)}s` : m > 0 ? `${m}m ${pad(s)}s` : `${s}s`;
}

// A cut diamond seen from the side: a flat top, a row of facets above the widest point and three running down to
// the tip. `golden` draws the golden diamond that drifts across the game.
export function DiamondIcon({ golden }) {
    let c = golden ?
        ['#ffe9a8', '#fff8dc', '#ffd56a', '#ffeebb', '#f3bb3c', '#e0a93a', '#ffcf4d', '#b07e1a', '#7a5410']
        : ['#8fe3ff', '#d4f6ff', '#6fd6fb', '#a9ebff', '#4fb9ea', '#35a7e0', '#57c8f5', '#2386c8', '#1b6fa8'];
    return (
        <svg className="clompy-diamond" viewBox="0 0 24 24" aria-hidden="true">
            <polygon points="6,3 2,9 8,9" fill={c[0]} />
            <polygon points="6,3 12,3 8,9" fill={c[1]} />
            <polygon points="12,3 8,9 16,9" fill={c[2]} />
            <polygon points="12,3 18,3 16,9" fill={c[3]} />
            <polygon points="18,3 22,9 16,9" fill={c[4]} />
            <polygon points="2,9 8,9 12,22" fill={c[5]} />
            <polygon points="8,9 16,9 12,22" fill={c[6]} />
            <polygon points="16,9 22,9 12,22" fill={c[7]} />
            <polygon points="6,3 18,3 22,9 12,22 2,9" fill="none" stroke={c[8]} strokeWidth="1" strokeLinejoin="round" />
        </svg>
    );
}

// Clompy as a paper takeaway cup: a tapered cup with a lid, a card sleeve with the face on it, and no handle.
// Drawn in the same box as the mug, standing on the same spot. Takes what ClompyMug takes.
function ToGoCup({ still, ...rest }) {
    let clip = `clompy-togo-body${still ? '-still' : ''}`;
    let outline = '#16222e';
    let cup = 'M68 86l22 162a16 16 0 0 0 16 14h74a16 16 0 0 0 16-14l22-162z';
    return (
        <svg viewBox="0 0 320 300" {...rest}>
            <defs>
                <clipPath id={clip}><path d={cup} /></clipPath>
            </defs>
            {still ? null : <path className="clompy-steam" d="M186 30c-10-9 10-15 0-26" />}
            {/* the cup, darker down its right side */}
            <path d={cup} fill="#f6eedd" />
            <g clipPath={`url(#${clip})`}>
                <rect x="176" y="80" width="50" height="190" fill="#e0d3b8" />
                <rect x="60" y="236" width="170" height="10" fill="powderblue" />
            </g>
            <path d={cup} fill="none" stroke={outline} strokeWidth="9" strokeLinejoin="round" />
            {/* the card sleeve */}
            <path d="M70 132h146l-11 86H81z" fill="#c98a4b" />
            <path d="M190 132h26l-11 86h-22z" fill="#a86f38" />
            <path d="M70 132h146l-11 86H81z" fill="none" stroke={outline} strokeWidth="8" strokeLinejoin="round" />
            {/* the lid: a rim, a dome and the hole to drink from */}
            <path d="M80 66q2-24 22-24h82q20 0 22 24z" fill="#ffffff" stroke={outline} strokeWidth="9" strokeLinejoin="round" />
            <rect x="56" y="64" width="174" height="28" rx="12" fill="#ffffff" stroke={outline} strokeWidth="9" />
            <rect x="196" y="68" width="30" height="20" rx="8" fill="#dfe6ea" />
            <ellipse cx="186" cy="50" rx="13" ry="5" fill={outline} />
            <path d="M96 56q4-8 14-8" fill="none" stroke="#dfe6ea" strokeWidth="5" strokeLinecap="round" />
            {/* the face, on the sleeve */}
            <ellipse cx="116" cy="166" rx="11" ry="15" fill={outline} />
            <ellipse cx="170" cy="166" rx="11" ry="15" fill={outline} />
            <circle cx="120" cy="160" r="4.5" fill="#ffffff" />
            <circle cx="174" cy="160" r="4.5" fill="#ffffff" />
            <ellipse cx="98" cy="190" rx="10" ry="6" fill="#ff9d8a" opacity="0.8" />
            <ellipse cx="188" cy="190" rx="10" ry="6" fill="#ff9d8a" opacity="0.8" />
            <path d="M128 188q15 16 30 0" fill="none" stroke={outline} strokeWidth="7" strokeLinecap="round" />
        </svg>
    );
}

// Clompy the mug, drawn as a game character: thick outline, flat shading, a face and rising steam.
// `skin` is the shop's id for the mug to draw; `still` leaves the steam out (the shop's small pictures).
// Anything else (the click handlers) goes on the drawing itself.
function ClompyMug({ skin, still, ...rest }) {
    if (skin === 'mug_togo') {
        return <ToGoCup still={still} {...rest} />;
    }
    let c = MUGS[skin] || MUGS.mug_classic;
    let clip = `clompy-mug-body-${skin}${still ? '-still' : ''}`;
    let body = 'M48 74v156a32 32 0 0 0 32 32h126a32 32 0 0 0 32-32V74z';
    let handle = 'M232 112h22a50 50 0 0 1 0 100h-22';
    return (
        <svg viewBox="0 0 320 300" {...rest}>
            <defs>
                <clipPath id={clip}><path d={body} /></clipPath>
            </defs>
            {still ? null :
                <>
                    <path className="clompy-steam" d="M104 40c-10-9 10-15 0-26" />
                    <path className="clompy-steam clompy-steam-2" d="M143 34c-10-9 10-15 0-26" />
                    <path className="clompy-steam clompy-steam-3" d="M182 40c-10-9 10-15 0-26" />
                </>
            }
            <path d={handle} fill="none" stroke={c.outline} strokeWidth="40" strokeLinecap="round" />
            <path d={handle} fill="none" stroke={c.shade} strokeWidth="24" strokeLinecap="round" />
            <path d="M236 105h18a57 57 0 0 1 40 17" fill="none" stroke={c.light} strokeWidth="6" strokeLinecap="round" />
            <path d={body} fill={c.body} />
            <g clipPath={`url(#${clip})`}>
                <rect x="186" y="60" width="60" height="210" fill={c.shade} />
                <rect x="48" y="226" width="190" height="40" fill={c.shade} opacity="0.7" />
                <rect x="64" y="96" width="14" height="96" rx="7" fill={c.light} />
                <rect x="64" y="204" width="14" height="16" rx="7" fill={c.light} />
                <rect x="48" y="232" width="190" height="12" fill={c.stripe} />
            </g>
            <path d={body} fill="none" stroke={c.outline} strokeWidth="9" strokeLinejoin="round" />
            <ellipse cx="143" cy="74" rx="95" ry="25" fill={c.light} stroke={c.outline} strokeWidth="9" />
            <ellipse cx="143" cy="77" rx="78" ry="16" fill="#6b3f24" />
            <path d="M82 76a62 12 0 0 1 60-9" fill="none" stroke="#a8683f" strokeWidth="5" strokeLinecap="round" />
            <ellipse cx="108" cy="158" rx="12" ry="16" fill={c.face} />
            <ellipse cx="170" cy="158" rx="12" ry="16" fill={c.face} />
            <circle cx="112" cy="151" r="5" fill="#ffffff" />
            <circle cx="174" cy="151" r="5" fill="#ffffff" />
            <ellipse cx="88" cy="184" rx="11" ry="7" fill={c.cheeks} opacity="0.75" />
            <ellipse cx="190" cy="184" rx="11" ry="7" fill={c.cheeks} opacity="0.75" />
            <path d="M122 182q17 18 34 0" fill="none" stroke={c.face} strokeWidth="7" strokeLinecap="round" />
        </svg>
    );
}

// Which of its looks a Coffee Machine or Cold Brew Tower level has: a new one at level 1 and at every fifth level
// (1-4, 5-9, 10-14, 15-19, 20-24, 25), or 0 before the first is bought
function machineTier(level) {
    return level < 1 ? 0 : Math.min(6, Math.floor(level / 5) + 1);
}

// How the espresso machines (looks 3 to 6) are coloured: steel, red, black and gold, then diamond
const ESPRESSO = {
    3: { body: '#c9d1d9', shade: '#9aa7b5', trim: '#5a6676', light: '#f2f5f8' },
    4: { body: '#d9463f', shade: '#a8322c', trim: '#e9eef2', light: '#ff8a80' },
    5: { body: '#2b2b31', shade: '#1b1b20', trim: '#ffd56a', light: '#5b5b66' },
    6: { body: '#6fd6fb', shade: '#35a7e0', trim: '#d4f6ff', light: '#d4f6ff' }
};

// The coffee machine that stands beside the mug once the Coffee Machine upgrade has been bought, in look `tier`:
//   1 a small drip brewer; 2 the same with a water tank and a light; 3 a steel espresso machine with a gauge;
//   4 a red one with two spouts and a steam wand; 5 black and gold with a bean hopper and lights;
//   6 cut from diamond, with a diamond on top
function CoffeeMachine({ tier, ...rest }) {
    let outline = '#16222e';
    let line = { stroke: outline, strokeWidth: 6, strokeLinejoin: 'round', strokeLinecap: 'round' };
    let cup = (x) => (
        <g>
            <path d={`M${x} 190h22l-3 16h-16z`} fill="#ffffff" {...line} strokeWidth="4" />
            <rect x={x + 9} y="176" width="4" height="12" rx="2" fill="#6b3f24" />
        </g>
    );
    if (tier <= 2) {
        let body = tier === 2 ? '#4f7fa8' : '#8a8f98';
        let shade = tier === 2 ? '#3b6385' : '#6c7079';
        return (
            <svg viewBox="0 0 200 240" {...rest}>
                <path className="clompy-steam" d="M82 118c-8-7 8-12 0-21" />
                {tier === 2 ?
                    <>
                        <rect x="116" y="26" width="42" height="40" rx="6" fill="#bfe9f5" opacity="0.85" {...line} />
                        <rect x="119" y="42" width="36" height="22" rx="3" fill="#6fc3e0" />
                    </>
                    : null}
                {/* the column at the back, the head that drips, and the plate the pot stands on */}
                <rect x="110" y="70" width="50" height="146" fill={shade} {...line} />
                <rect x="38" y="60" width="124" height="40" rx="9" fill={body} {...line} />
                <rect x="76" y="100" width="12" height="12" fill={shade} {...line} strokeWidth="4" />
                <rect x="34" y="210" width="132" height="22" rx="7" fill={body} {...line} />
                {/* the glass pot, half full */}
                <path d="M54 130h56v64a14 14 0 0 1-14 14H68a14 14 0 0 1-14-14z" fill="#dff3f7" opacity="0.9" />
                <path d="M54 164h56v30a14 14 0 0 1-14 14H68a14 14 0 0 1-14-14z" fill="#6b3f24" />
                <path d="M54 130h56v64a14 14 0 0 1-14 14H68a14 14 0 0 1-14-14z" fill="none" {...line} />
                <path d="M54 142h-8a8 8 0 0 0-8 8v22a8 8 0 0 0 8 8h8" fill="none" {...line} />
                <rect x="50" y="124" width="64" height="10" rx="4" fill={outline} />
                {tier === 2 ? <circle cx="135" cy="118" r="7" fill="#62e06a" {...line} strokeWidth="4" /> : null}
                <rect x="124" y="140" width="22" height="8" rx="4" fill={outline} opacity="0.5" />
            </svg>
        );
    }
    let c = ESPRESSO[tier];
    return (
        <svg viewBox="0 0 200 240" {...rest}>
            <path className="clompy-steam" d={tier >= 4 ? 'M170 150c-8-7 8-12 0-21' : 'M82 168c-8-7 8-12 0-21'} />
            {tier === 5 ?
                <>
                    {/* the hopper, full of beans */}
                    <path d="M66 18h68l-10 42H76z" fill="#dff3f7" opacity="0.85" {...line} />
                    <path d="M71 38h58l-5 22H76z" fill="#6b3f24" />
                    <rect x="60" y="10" width="80" height="12" rx="5" fill={c.trim} {...line} />
                </>
                : null}
            {tier === 6 ?
                <g className="clompy-machine-gem">
                    <polygon points="84,14 116,14 130,32 100,62 70,32" fill="#d4f6ff" {...line} />
                    <polygon points="84,14 100,32 116,14" fill="#ffffff" />
                    <polygon points="70,32 130,32 100,62" fill="#8fe3ff" />
                    <polygon points="84,14 116,14 130,32 100,62 70,32" fill="none" {...line} />
                </g>
                : null}
            {/* the feet and drip tray, the dark space the cups stand in, and the body above it */}
            <rect x="22" y="212" width="156" height="20" rx="7" fill={c.trim} {...line} />
            <rect x="30" y="172" width="140" height="42" fill="#20262e" {...line} />
            <rect x="34" y="204" width="132" height="8" rx="3" fill={outline} opacity="0.6" />
            <rect x="18" y="70" width="164" height="108" rx="13" fill={c.body} {...line} />
            <rect x="136" y="73" width="43" height="102" rx="10" fill={c.shade} />
            <rect x="30" y="84" width="10" height="56" rx="5" fill={c.light} opacity="0.8" />
            <rect x="18" y="70" width="164" height="108" rx="13" fill="none" {...line} />
            <rect x="12" y="56" width="176" height="22" rx="8" fill={c.trim} {...line} />
            {tier === 6 ?
                <>
                    <polygon points="52,96 64,84 76,96 64,116" fill="#d4f6ff" opacity="0.9" />
                    <polygon points="118,128 128,118 138,128 128,146" fill="#d4f6ff" opacity="0.9" />
                </>
                : null}
            {/* the pressure gauge, and from the black machine up a row of lights */}
            <circle cx="100" cy="116" r="19" fill="#ffffff" {...line} />
            <path d={tier >= 5 ? 'M100 116l12-9' : 'M100 116l-9-10'} fill="none" stroke="#d9463f" strokeWidth="4" strokeLinecap="round" />
            <circle cx="100" cy="116" r="3" fill={outline} />
            {tier >= 5 ?
                <>
                    <circle cx="52" cy="150" r="6" fill="#62e06a" {...line} strokeWidth="3" />
                    <circle cx="72" cy="150" r="6" fill="#ffd56a" {...line} strokeWidth="3" />
                    <circle cx="148" cy="150" r="6" fill="#ff7a1a" {...line} strokeWidth="3" />
                </>
                : null}
            {/* a spout with its handle and a cup under it; two from the red machine up, with a steam wand */}
            <rect x={tier >= 4 ? 50 : 76} y="172" width="32" height="12" rx="3" fill={c.trim} {...line} strokeWidth="4" />
            {cup(tier >= 4 ? 55 : 81)}
            {tier >= 4 ?
                <>
                    <rect x="104" y="172" width="32" height="12" rx="3" fill={c.trim} {...line} strokeWidth="4" />
                    {cup(109)}
                    <path d="M176 150q10 4 6 24l-6 22" fill="none" {...line} />
                    <path d="M176 150q10 4 6 24l-6 22" fill="none" stroke={c.trim} strokeWidth="2" strokeLinecap="round" />
                </>
                : null}
        </svg>
    );
}

// How the cold brew tower's frame and fittings are coloured in each of its looks: pine, oak, walnut with brass,
// steel, black and gold, then diamond
const TOWERS = {
    1: { post: '#d9a066', shade: '#c08a52', plank: '#e6b983', tap: '#5a6676' },
    2: { post: '#b9793f', shade: '#9a5f2e', plank: '#c98a4b', tap: '#5a6676' },
    3: { post: '#6b3f24', shade: '#4a2a18', plank: '#8a5230', tap: '#e0aa3e' },
    4: { post: '#c9d1d9', shade: '#9aa7b5', plank: '#e9eef2', tap: '#5a6676' },
    5: { post: '#2b2b31', shade: '#1b1b20', plank: '#ffd56a', tap: '#ffd56a' },
    6: { post: '#6fd6fb', shade: '#35a7e0', plank: '#d4f6ff', tap: '#d4f6ff' }
};

// The cold brew tower that stands on the other side of the mug once that upgrade has been bought (the backend
// stores it as `barista`): a frame holding a globe of iced water, which drips down into a jar. It is what earns
// diamonds while the player is away. Like the coffee machine it has a look for each `tier` (see machineTier):
//   1 a pine frame, the globe dripping straight into the jar; 2 oak, with a chamber of coffee grounds on a shelf;
//   3 walnut with a brass tap and a glass coil; 4 steel, with a valve wheel and a measuring jar;
//   5 black and gold with lights; 6 cut from diamond, with a diamond in the globe in place of the ice
function ColdBrewTower({ tier, ...rest }) {
    let c = TOWERS[tier] || TOWERS[1];
    let outline = '#16222e';
    let line = { stroke: outline, strokeWidth: 6, strokeLinejoin: 'round', strokeLinecap: 'round' };
    return (
        <svg viewBox="0 0 200 240" {...rest}>
            {/* the frame: two posts, a top and a base, and from the second look a shelf */}
            <rect x="36" y="26" width="14" height="196" fill={c.post} {...line} />
            <rect x="150" y="26" width="14" height="196" fill={c.shade} {...line} />
            <rect x="26" y="16" width="148" height="16" rx="5" fill={c.plank} {...line} />
            <rect x="26" y="216" width="148" height="16" rx="5" fill={c.plank} {...line} />
            {tier >= 2 ? <rect x="46" y="118" width="108" height="10" fill={c.shade} {...line} strokeWidth="4" /> : null}
            {tier >= 5 ?
                <>
                    <circle cx="48" cy="224" r="4" fill="#62e06a" />
                    <circle cx="62" cy="224" r="4" fill={tier === 6 ? '#ffffff' : '#ff7a1a'} />
                    <circle cx="152" cy="224" r="4" fill="#62e06a" />
                </>
                : null}
            {/* the globe of iced water (or, in the last look, of water round a diamond), and the tap under it */}
            <circle cx="100" cy="72" r="34" fill="#e9f8fb" opacity="0.9" />
            <path d="M67 80a34 34 0 0 0 66 0z" fill="#8fd3ee" />
            {tier === 6 ?
                <g className="clompy-machine-gem">
                    <polygon points="90,56 110,56 120,68 100,92 80,68" fill="#d4f6ff" stroke="#1b6fa8" strokeWidth="3" strokeLinejoin="round" />
                    <polygon points="90,56 100,68 110,56" fill="#ffffff" />
                    <polygon points="80,68 120,68 100,92" fill="#6fd6fb" />
                </g>
                :
                <>
                    <rect x="80" y="64" width="14" height="14" rx="3" fill="#ffffff" stroke="#bfe9f5" strokeWidth="2" transform="rotate(-12 87 71)" />
                    <rect x="104" y="68" width="13" height="13" rx="3" fill="#ffffff" stroke="#bfe9f5" strokeWidth="2" transform="rotate(14 110 74)" />
                </>
            }
            <circle cx="100" cy="72" r="34" fill="none" {...line} />
            <path d="M78 54a26 26 0 0 1 16-10" fill="none" stroke="#ffffff" strokeWidth="5" strokeLinecap="round" />
            <rect x="94" y="104" width="12" height="16" fill={c.tap} {...line} strokeWidth="4" />
            {tier >= 4 ?
                <>
                    {/* a wheel to set the drip */}
                    <path d="M106 112h14" fill="none" {...line} strokeWidth="4" />
                    <circle cx="126" cy="112" r="8" fill={tier === 4 ? '#d9463f' : c.tap} {...line} strokeWidth="4" />
                </>
                : null}
            {tier >= 2 ?
                <>
                    {/* the chamber of grounds */}
                    <rect x="76" y="130" width="48" height="36" rx="8" fill="#e9f8fb" opacity="0.9" />
                    <rect x="79" y="142" width="42" height="21" rx="5" fill="#4a2a18" />
                    <rect x="76" y="130" width="48" height="36" rx="8" fill="none" {...line} />
                </>
                :
                <path d="M100 120v52" fill="none" {...line} strokeWidth="4" />
            }
            {/* down to the jar: a straight pipe, or from the third look a glass coil */}
            {tier >= 3 ?
                <>
                    <path d="M100 166q14 3 0 7q-14 3 0 7q14 3 0 6" fill="none" stroke={outline} strokeWidth="7" strokeLinecap="round" />
                    <path d="M100 166q14 3 0 7q-14 3 0 7q14 3 0 6" fill="none" stroke="#bfe9f5" strokeWidth="3" strokeLinecap="round" />
                </>
                : tier === 2 ? <path d="M100 166v10" fill="none" {...line} strokeWidth="4" /> : null}
            {tier >= 3 ? null : <circle className="clompy-drip" cx="100" cy="178" r="4" fill="#6b3f24" />}
            <path d="M70 186h60v18a12 12 0 0 1-12 12H82a12 12 0 0 1-12-12z" fill="#e9f8fb" opacity="0.9" />
            <path d={tier >= 4 ? 'M70 192h60v12a12 12 0 0 1-12 12H82a12 12 0 0 1-12-12z' : 'M70 198h60v6a12 12 0 0 1-12 12H82a12 12 0 0 1-12-12z'} fill="#6b3f24" />
            <path d="M70 186h60v18a12 12 0 0 1-12 12H82a12 12 0 0 1-12-12z" fill="none" {...line} />
            {tier >= 4 ? <path d="M122 194h-7M122 202h-7M122 210h-7" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" opacity="0.8" /> : null}
        </svg>
    );
}

// The picture on the sound button: a speaker with sound coming out of it, and when `muted` a red circle with a line
// through it over the top
function SoundIcon({ muted }) {
    return (
        <svg viewBox="0 0 32 32" aria-hidden="true">
            <path d="M5 12h5l7-6v20l-7-6H5z" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            <path d="M21 12a6 6 0 0 1 0 8M24 8.5a11 11 0 0 1 0 15" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            {muted ?
                <>
                    <circle cx="16" cy="16" r="13" fill="none" stroke="#ff3b30" strokeWidth="3" />
                    <path d="M6.8 6.8l18.4 18.4" fill="none" stroke="#ff3b30" strokeWidth="3" strokeLinecap="round" />
                </>
                : null}
        </svg>
    );
}

// What stands on the counter beside the mug for an upgrade the player owns: its picture, under a tag with its
// level and what it earns, so neither needs the shop opening. `side` is 'left' or 'right' of the mug; `when` says
// when it earns, if that isn't all the time.
function Prop({ side, name, level, earns, when, children }) {
    return (
        <div className={`clompy-prop clompy-prop-${side}`} role="img" aria-label={`${name}, level ${level}: ${earns} diamonds${when ? ` ${when}` : ''}`}>
            <div className="clompy-prop-tag" aria-hidden="true">
                <span className="clompy-prop-level">Lv {level}</span>
                <span className="clompy-prop-earns"><DiamondIcon />{earns}</span>
                {when ? <span className="clompy-prop-when">{when}</span> : null}
            </div>
            {children}
        </div>
    );
}

// A panel shown over the game (the shop, the stats). Closes on its button, a click outside it or Escape.
function Panel({ title, wide, closeLabel, onClose, children }) {
    React.useEffect(() => {
        let onKeyDown = (e) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onClose]);
    return (
        <div className="clompy-panel" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className={`clompy-panel-body${wide ? ' is-wide' : ''}`}>
                <h2>{title}</h2>
                {children}
                <button type="button" className="clompy-button" onClick={onClose} autoFocus>{closeLabel || 'Close'}</button>
            </div>
        </div>
    );
}

export class ClompyClickerGame extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showHelp: false,
            showShare: false,
            // A logged-in player's numbers as saved on the backend: diamonds they have to spend (which the backend
            // calls coins), diamonds ever earned, clicks ever made and seconds played. What is shown is these plus
            // what the clicks and seconds not yet sent will earn. A guest's count this visit only.
            saved: { coins: 0, allTime: 0, clicks: 0, seconds: 0 },
            // What the player has bought in the shop, and what that makes a click and a second worth
            game: STARTING_GAME,
            // What the shop sells: { upgrades, slots, cosmetics }, once loaded
            shop: null,
            // the id of the item being bought or put on, and the last refusal from the shop
            buying: null,
            shopError: null,
            pending: 0,
            pendingSeconds: 0,
            // 'shop' or 'stats' while that panel is open
            panel: null,
            // what floats up from recent clicks: [{ id, x, y, text, big }], placed as percentages of the scene
            pops: [],
            // the golden diamond while one is on screen: { x, y } as percentages of the scene
            golden: null,
            // what the Cold Brew Tower earned while the player was away, until they have seen it: { seconds, counted, earned }
            away: null,
            pressed: false,
            // the game's sounds are switched off (remembered between visits)
            muted: isMuted(),
            // how loud they are, from 0 to 1 (also remembered)
            volume: getVolume(),
            // Logged-in players' diamonds are saved and count towards the leaderboard; anyone else plays for fun
            loggedIn: localStorage.getItem('token') !== null,
        }
        // clicks made, and seconds played, that a save under way is carrying
        this.sending = 0;
        this.sendingSeconds = 0;
        this.savePromise = Promise.resolve();
        this.nextPop = 0;
        // seconds until the next golden diamond appears, or until the one on screen gets away
        this.goldenWait = between(GOLDEN_FIRST);
        this.stage = React.createRef();
    }

    componentDidMount() {
        if (this.state.loggedIn) {
            this.collectAway();
        }
        api.games.getClickerShop().then((shop) => {
            if (!this.unmounted) {
                this.setState({ shop });
            }
        }).catch((err) => console.log('Error loading the shop: ', err));
        this.timer = setInterval(this.sync, SYNC_MS);
        this.clock = setInterval(this.tick, 1000);
        document.addEventListener('visibilitychange', this.visibilityChanged);
    }

    componentWillUnmount() {
        clearInterval(this.timer);
        clearInterval(this.clock);
        document.removeEventListener('visibilitychange', this.visibilityChanged);
        this.unmounted = true;
        this.save(true);
    }

    // Load the player's game, collecting whatever their Cold Brew Tower earned since they were last here. Called when the
    // game opens and when it comes back into view after a while.
    collectAway() {
        api.games.collectClickerAway().then((mine) => {
            this.applyGame(mine);
            if (!this.unmounted && mine.away && mine.away.earned > 0) {
                this.setState({ away: mine.away });
            }
        }).catch((err) => this.syncFailed(err));
    }

    // Leaving the tab saves what is unsent; coming back after a while collects what was earned meanwhile
    visibilityChanged = () => {
        if (document.hidden) {
            this.hiddenAt = Date.now();
            this.save(true);
        } else if (this.hiddenAt && Date.now() - this.hiddenAt >= AWAY_CHECK_MS && this.state.loggedIn) {
            this.hiddenAt = null;
            // anything still unsent goes first, so its time isn't taken for time away
            this.save(true).then(() => !this.unmounted && this.collectAway());
        }
    }

    // Take the player's game from a backend answer: their numbers and their shop purchases
    applyGame(answer) {
        if (this.unmounted) {
            return;
        }
        this.setState({
            saved: { coins: answer.coins, allTime: answer.allTime, clicks: answer.clicks, seconds: answer.seconds },
            game: {
                levels: answer.levels, owned: answer.owned, equipped: answer.equipped, perClick: answer.perClick, perSecond: answer.perSecond,
                awayPerHour: answer.awayPerHour, awayMaxSeconds: answer.awayMaxSeconds
            }
        });
    }

    // Once a second, while the game is on screen: time played counts up, and the golden diamond comes and goes
    tick = () => {
        if (document.hidden) {
            return;
        }
        this.goldenWait--;
        let golden = this.state.golden;
        if (this.goldenWait <= 0) {
            if (golden) {
                golden = null;
                this.goldenWait = between(GOLDEN_GAP);
            } else {
                // somewhere in the sky, clear of the count at the top and the counter at the bottom
                golden = { x: 10 + Math.random() * 80, y: 22 + Math.random() * 40 };
                this.goldenWait = GOLDEN_STAYS;
            }
        }
        this.setState((s) => ({ pendingSeconds: s.pendingSeconds + 1, golden }));
    }

    sync = () => {
        this.save(false);
    }

    // Send the clicks made and the time played since the last save. Time alone is sent once enough has built up,
    // when the game is being left, or with `now`. Resolves when the save is over, whether or not it worked.
    save(now) {
        let clicks = this.state.pending;
        let seconds = this.state.pendingSeconds;
        let due = now || this.unmounted || document.hidden || seconds >= IDLE_SYNC_SECONDS;
        if (!this.state.loggedIn || this.saving || (clicks === 0 && (seconds === 0 || !due))) {
            return this.savePromise;
        }
        this.saving = true;
        this.sending = clicks;
        this.sendingSeconds = seconds;
        if (!this.unmounted) {
            this.setState({ pending: 0, pendingSeconds: 0 });
        }
        this.savePromise = api.games.addClickerClicks(clicks, seconds).then((result) => {
            this.saving = false;
            this.sending = 0;
            this.sendingSeconds = 0;
            this.applyGame(result);
        }).catch((err) => {
            // keep the clicks and the time to try again
            let unsent = this.sending;
            let unsentSeconds = this.sendingSeconds;
            this.saving = false;
            this.sending = 0;
            this.sendingSeconds = 0;
            if (!this.unmounted) {
                this.setState((s) => ({ pending: s.pending + unsent, pendingSeconds: s.pendingSeconds + unsentSeconds }));
                this.syncFailed(err);
            }
        });
        return this.savePromise;
    }

    syncFailed(err) {
        if (err.response && err.response.status === 401) {
            // the login ran out: carry on as a guest with the numbers on screen
            this.setState({ loggedIn: false });
        } else {
            console.log('Error saving diamonds: ', err);
        }
    }

    // Buy something from the shop, or put on a cosmetic already owned. Clicks not yet saved are sent first, so
    // they are paid at what a click was worth when they were made.
    shopAction(item, action) {
        if (this.state.buying || !this.state.loggedIn) {
            return;
        }
        this.setState({ buying: item, shopError: null });
        this.savePromise.then(() => this.save(true)).then(() => action(item)).then((result) => {
            this.applyGame(result);
            this.setState({ buying: null });
        }).catch((err) => {
            let message = err.response && err.response.data && err.response.data.message;
            this.setState({ buying: null, shopError: message || "The shop couldn't do that. Please try again." });
            if (err.response && err.response.status === 401) {
                this.setState({ loggedIn: false, shopError: 'Your login has expired. Log in again to use the shop.' });
            }
        });
    }

    buy = (item) => this.shopAction(item, (id) => api.games.buyClickerItem(id));

    equip = (item) => this.shopAction(item, (id) => api.games.equipClickerItem(id));

    // The golden diamond was clicked before it got away
    catchGolden = () => {
        let at = this.state.golden;
        if (!at) {
            return;
        }
        this.goldenWait = between(GOLDEN_GAP);
        this.setState({ golden: null });
        if (!this.state.muted) {
            playGoldenCatch();
        }
        if (!this.state.loggedIn) {
            this.addPop(at.x, at.y, GOLDEN_GUEST_REWARD, true);
            this.setState((s) => ({ saved: { ...s.saved, coins: s.saved.coins + GOLDEN_GUEST_REWARD, allTime: s.saved.allTime + GOLDEN_GUEST_REWARD } }));
            return;
        }
        api.games.claimClickerGolden().then((result) => {
            this.applyGame(result);
            if (!this.unmounted) {
                this.addPop(at.x, at.y, result.reward, true);
            }
        }).catch((err) => {
            // refused (one was claimed too recently, perhaps in another tab): it simply got away
            if (!err.response || err.response.status !== 409) {
                this.syncFailed(err);
            }
        });
    }

    // Float a number up from x, y (percentages of the scene), in the click effect being worn
    addPop(x, y, amount, big) {
        let mark = EFFECTS[this.state.game.equipped.effect] || '';
        let pop = { id: this.nextPop++, x, y, text: `${mark}+${amount.toLocaleString()}`, big: Boolean(big) };
        // only the latest few are kept, in case their animations never finish (as with reduced motion)
        this.setState((s) => ({ pops: [...s.pops.slice(-11), pop] }));
    }

    popDone = (id) => {
        this.setState((s) => ({ pops: s.pops.filter((pop) => pop.id !== id) }));
    }

    // Count a click on the mug, with its diamonds floating up from x, y (percentages of the scene)
    click(x, y) {
        this.setState((s) => ({ pending: s.pending + 1, pressed: true }));
        this.addPop(x, y, this.state.game.perClick);
        if (!this.state.muted) {
            playMugClick();
        }
    }

    clompyClicked = (e) => {
        // only the main button of a mouse; touches and pens always count
        if (e.pointerType === 'mouse' && e.button !== 0) {
            return;
        }
        // the number rises from where the mug was clicked
        let box = this.stage.current.getBoundingClientRect();
        this.click((e.clientX - box.left) / box.width * 100, (e.clientY - box.top) / box.height * 100);
    }

    clompyReleased = () => {
        if (this.state.pressed) {
            this.setState({ pressed: false });
        }
    }

    // Space and Enter click the mug too
    clompyKeyDown = (e) => {
        if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
            e.preventDefault();
            this.click(40 + Math.random() * 20, 42);
        }
    }

    toggleSound = () => {
        let muted = !this.state.muted;
        setMuted(muted);
        this.setState({ muted });
    }

    // The volume slider was moved. Moving it turns the sound back on if it was muted.
    volumeChanged = (e) => {
        let volume = Number(e.target.value) / 100;
        setVolume(volume);
        setMuted(false);
        this.setState({ volume, muted: false });
    }

    // The slider was let go of: play a click, so the new volume can be heard
    volumeChosen = () => {
        if (!this.state.muted) {
            playMugClick();
        }
    }

    closeAway = () => {
        this.setState({ away: null });
    }

    closePanel = () => {
        this.setState({ panel: null, shopError: null });
    }

    getShareURL() {
        return window.location.origin + window.location.pathname;
    }

    toggleHelp() {
        this.setState({
            showHelp: !this.state.showHelp
        });
    }

    toggleShare() {
        this.setState({
            showShare: !this.state.showShare
        });
    }

    // A small picture of a cosmetic, for its tile in the shop
    renderPreview(cosmetic) {
        if (cosmetic.slot === 'mug') {
            return <ClompyMug skin={cosmetic.id} still className="clompy-preview-mug" aria-hidden="true" />;
        }
        if (cosmetic.slot === 'background') {
            return <span className={`clompy-preview-bg clompy-${cosmetic.id}`} aria-hidden="true"></span>;
        }
        return <span className={`clompy-preview-fx clompy-pop-${cosmetic.id}`} aria-hidden="true">{EFFECTS[cosmetic.id] || ''}+1</span>;
    }

    renderShop(diamonds) {
        let { shop, game, buying, shopError, loggedIn } = this.state;
        if (!shop) {
            return <p className="clompy-panel-empty">Loading the shop...</p>;
        }
        let price = (cost) => <><DiamondIcon />{cost.toLocaleString()}</>;
        return (
            <>
                <p className="clompy-shop-balance" role="status" aria-label={`${diamonds.toLocaleString()} diamonds to spend`}><DiamondIcon />{diamonds.toLocaleString()}</p>
                {loggedIn ? null : <p className="clompy-panel-note"><Link to="/login?next=/clompyclicker">Log in</Link> to buy from the shop.</p>}
                {shopError ? <p className="clompy-shop-error" role="alert">{shopError}</p> : null}

                <h3>Upgrades</h3>
                <ul className="clompy-upgrades">
                    {shop.upgrades.map((upgrade) => {
                        let level = game.levels[upgrade.id] || 0;
                        let maxed = level >= upgrade.maxLevel;
                        let cost = maxed ? 0 : upgrade.costs[level];
                        return (
                            <li key={upgrade.id}>
                                <div>
                                    <strong>{upgrade.name}</strong> <span className="clompy-level">Level {level} / {upgrade.maxLevel}</span>
                                    <p>{upgrade.description}</p>
                                </div>
                                <button type="button" className="clompy-button clompy-buy" disabled={maxed || !loggedIn || Boolean(buying) || diamonds < cost}
                                    aria-label={maxed ? `${upgrade.name} is fully upgraded` : `Buy ${upgrade.name} level ${level + 1} for ${cost.toLocaleString()} diamonds`}
                                    onClick={() => this.buy(upgrade.id)}>
                                    {maxed ? 'Maxed' : price(cost)}
                                </button>
                            </li>
                        );
                    })}
                </ul>

                {shop.slots.map((slot) => (
                    <React.Fragment key={slot.id}>
                        <h3>{slot.name}</h3>
                        <ul className="clompy-cosmetics">
                            {shop.cosmetics.filter((cosmetic) => cosmetic.slot === slot.id).map((cosmetic) => {
                                let owned = game.owned.includes(cosmetic.id);
                                let wearing = game.equipped[slot.id] === cosmetic.id;
                                return (
                                    <li key={cosmetic.id} className={wearing ? 'is-equipped' : ''}>
                                        {this.renderPreview(cosmetic)}
                                        <span className="clompy-cosmetic-name">{cosmetic.name}</span>
                                        {wearing ?
                                            <button type="button" className="clompy-button clompy-buy" disabled>In use</button>
                                            : owned ?
                                                <button type="button" className="clompy-button clompy-buy" disabled={!loggedIn || Boolean(buying)} aria-label={`Use ${cosmetic.name}`} onClick={() => this.equip(cosmetic.id)}>Use</button>
                                                :
                                                <button type="button" className="clompy-button clompy-buy" disabled={!loggedIn || Boolean(buying) || diamonds < cosmetic.cost}
                                                    aria-label={`Buy ${cosmetic.name} for ${cosmetic.cost.toLocaleString()} diamonds`} onClick={() => this.buy(cosmetic.id)}>
                                                    {price(cosmetic.cost)}
                                                </button>
                                        }
                                    </li>
                                );
                            })}
                        </ul>
                    </React.Fragment>
                ))}
            </>
        );
    }

    render() {
        // while a save is under way its clicks and seconds are in neither count
        let { saved, game, panel, golden, away } = this.state;
        let tier = machineTier(game.levels.machine || 0);
        let towerTier = machineTier(game.levels.barista || 0);
        // muted, or the volume turned all the way down
        let silent = this.state.muted || this.state.volume === 0;
        let unsavedClicks = this.state.pending + this.sending;
        let unsavedSeconds = this.state.pendingSeconds + this.sendingSeconds;
        let unsaved = unsavedClicks * game.perClick + unsavedSeconds * game.perSecond;
        let diamonds = saved.coins + unsaved;
        let stats = [
            { label: 'Current diamonds', value: diamonds.toLocaleString(), diamond: true },
            { label: 'All-time diamonds', value: (saved.allTime + unsaved).toLocaleString(), diamond: true },
            { label: 'Diamonds per click', value: game.perClick.toLocaleString(), diamond: true },
            { label: 'Diamonds per second', value: game.perSecond.toLocaleString(), diamond: true },
            { label: 'Diamonds per hour away', value: game.awayPerHour.toLocaleString(), diamond: true },
            { label: 'Longest time away', value: formatAway(game.awayMaxSeconds) },
            { label: 'All-time clicks', value: (saved.clicks + unsavedClicks).toLocaleString() },
            { label: 'Time played', value: formatDuration(saved.seconds + unsavedSeconds) }
        ];

        let helpPopup = (
            <div className='wordle-popup' onClick={this.toggleHelp.bind(this)}>
                <div className='wordle-popup-content'>
                    <h2>How to play</h2>
                    <p>
                        The goal of the game is to collect Diamonds.
                        <br />
                        Diamonds can be collected by clicking the mug.
                        <br />
                        Catch a golden diamond when one appears for a bonus.
                        <br />
                        Set up a Cold Brew Tower from the shop to keep earning while you are away.
                        <br />
                        Spend Diamonds in the shop on upgrades and new looks.
                        <br />
                        Compete against others to gather the most Diamonds.
                    </p>
                </div>
            </div>
        );

        let sharePopup = (
            <div className='wordle-popup' onClick={this.toggleShare.bind(this)}>
                <div className='wordle-popup-content'>
                    <h2>Share this game</h2>
                    <div className='button-row'>
                        <a className='wordle-button' href={`https://twitter.com/intent/tweet?text=I%20just%20played%20a%20word%20puzzle%20with%20Wordle%20on%20clomp.io!%20Check%20it%20out%20at%20${encodeURIComponent(this.getShareURL())}`} target='_blank' rel='noopener noreferrer'
                            style={
                                {
                                    backgroundColor: '#1DA1F2',
                                    color: 'white'
                                }
                            }>
                            {/* twitter icon */}
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                <path fill="#FFFFFF" d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"></path>

                            </svg>
                        </a>
                        <div className='wordle-button' onClick={ () => {
                                navigator.clipboard.writeText(this.getShareURL());
                            }}>
                            Copy link &nbsp;
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                <path fill="var(--color-tone-1)" d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"></path>
                            </svg>
                        </div>
                    </div>
                </div>
            </div>
        );
        return (
            <>
                <Navbar />
                <div className="wordle">
                    <div className="wordle-header">
                        <h1>Clompy Clicker</h1>
                        <div onClick={this.toggleHelp.bind(this)}>
                            <svg height="24" viewBox="0 0 24 24" width="24">
                                <path fill="var(--color-tone-1)" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"></path>
                            </svg>
                        </div>
                    </div>

                    <div className="clompyclicker">
                        <div className="game-area">
                            {/* the scene: Clompy on a counter, light fanning out behind, in the background being worn */}
                            <div className={`clompy-stage clompy-${game.equipped.background}`} ref={this.stage}>
                                <div className="clompy-rays" aria-hidden="true"></div>
                                {/* the sound button; pointing at it (or tabbing to it) slides out a volume slider beside it */}
                                <div className="clompy-sound-box">
                                    <input type="range" className="clompy-volume" min="0" max="100" step="5" aria-label="Volume"
                                        value={Math.round(this.state.volume * 100)} onChange={this.volumeChanged}
                                        onMouseUp={this.volumeChosen} onTouchEnd={this.volumeChosen} onKeyUp={this.volumeChosen} />
                                    <button type="button" className="clompy-sound" aria-pressed={silent} onClick={this.toggleSound}
                                        aria-label={this.state.muted ? 'Sound is off: turn it on' : 'Sound is on: turn it off'} title={this.state.muted ? 'Sound off' : 'Sound on'}>
                                        <SoundIcon muted={silent} />
                                    </button>
                                </div>
                                {/* the coffee machine, once one has been bought; a new look pops in afresh */}
                                {tier > 0 ?
                                    <Prop side="left" name="Coffee Machine" level={game.levels.machine} earns={`+${game.perSecond.toLocaleString()}/s`}>
                                        <CoffeeMachine key={tier} tier={tier} className={`clompy-machine clompy-machine-${tier}`} aria-hidden="true" />
                                    </Prop>
                                    : null}
                                {/* and on the other side the cold brew tower, which earns while the player is away */}
                                {towerTier > 0 ?
                                    <Prop side="right" name="Cold Brew Tower" level={game.levels.barista} earns={`+${game.awayPerHour.toLocaleString()}/h`} when="while away">
                                        <ColdBrewTower key={towerTier} tier={towerTier} className={`clompy-machine clompy-machine-${towerTier}`} aria-hidden="true" />
                                    </Prop>
                                    : null}
                                <div className="clompy-diamonds" role="status" aria-label={`${diamonds.toLocaleString()} diamonds`}>
                                    <DiamondIcon />
                                    <span>{diamonds.toLocaleString()}</span>
                                </div>
                                {/* the mug sits in a box of fixed size and squashes inside it when pressed, so nothing around it moves */}
                                <div className="clompy-mug">
                                    <ClompyMug skin={game.equipped.mug} className={`clompy-mug-art${this.state.pressed ? ' is-pressed' : ''}`}
                                        role="button" tabIndex="0" aria-label="Clompy the mug: click to collect diamonds"
                                        onPointerDown={this.clompyClicked} onPointerUp={this.clompyReleased} onPointerLeave={this.clompyReleased} onPointerCancel={this.clompyReleased}
                                        onKeyDown={this.clompyKeyDown} onKeyUp={this.clompyReleased} onBlur={this.clompyReleased} />
                                </div>
                                {golden ?
                                    <button type="button" className="clompy-golden" style={{ left: `${golden.x}%`, top: `${golden.y}%` }}
                                        aria-label="Golden diamond: click it before it gets away" onClick={this.catchGolden}>
                                        <DiamondIcon golden />
                                    </button>
                                    : null}
                                {this.state.pops.map((pop) => (
                                    <span key={pop.id} className={`clompy-pop clompy-pop-${game.equipped.effect}${pop.big ? ' is-big' : ''}`} aria-hidden="true"
                                        style={{ left: `${pop.x}%`, top: `${pop.y}%` }} onAnimationEnd={() => this.popDone(pop.id)}>{pop.text}</span>
                                ))}
                            </div>
                            <div className="clompy-buttons">
                                <button type="button" className="clompy-button" onClick={() => this.setState({ panel: 'shop' })}>Shop</button>
                                <button type="button" className="clompy-button" onClick={() => this.setState({ panel: 'stats' })}>Stats</button>
                                <Link className="clompy-button clompy-button-primary" to="/leaderboard?game=clompyclicker">Leaderboard</Link>
                            </div>
                            {this.state.loggedIn ? null :
                                <p className="clompy-note"><Link to="/login?next=/clompyclicker">Log in</Link> to save your diamonds, use the shop and join the leaderboard.</p>
                            }
                        </div>
                    </div>

                    {away ?
                        <Panel title="Welcome back!" closeLabel="Collect" onClose={this.closeAway}>
                            <p className="clompy-away">While you were away for {formatAway(away.seconds)}, your Cold Brew Tower collected</p>
                            <p className="clompy-away-earned"><DiamondIcon />{away.earned.toLocaleString()}</p>
                            {away.counted < away.seconds ?
                                <p className="clompy-panel-note">Your Cold Brew Tower drips for up to {formatAway(game.awayMaxSeconds)} at a time. A Thermos from the shop makes that longer.</p>
                                : null}
                        </Panel>
                        : null}
                    {panel === 'shop' ?
                        <Panel title="Shop" wide onClose={this.closePanel}>
                            {this.renderShop(diamonds)}
                        </Panel>
                        : null}
                    {panel === 'stats' ?
                        <Panel title="Stats" onClose={this.closePanel}>
                            <dl className="clompy-stats">
                                {stats.map((stat) => (
                                    <div key={stat.label}>
                                        <dt>{stat.label}</dt>
                                        <dd>{stat.diamond ? <DiamondIcon /> : null}{stat.value}</dd>
                                    </div>
                                ))}
                            </dl>
                            {this.state.loggedIn ? null : <p className="clompy-panel-note">These count this visit only. Log in to keep them.</p>}
                        </Panel>
                        : null}
                    {this.state.showHelp ? helpPopup : null}
                    {this.state.showShare ? sharePopup : null}
                </div>
            </>
        )
    }

}
